import { useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Recaptcha } from "@/components/Recaptcha";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, verify2fa, requestEmailCode, loginWithEmailCode } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextParam = params.get("next");
  const next = nextParam && nextParam.startsWith("/") ? nextParam : null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef(null);
  // Login mode: "password" or "emailcode"
  const [mode, setMode] = useState("password");
  const [codeSent, setCodeSent] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  // 2FA step
  const [twoFactor, setTwoFactor] = useState(null); // {user_id, phone_hint}
  const [code, setCode] = useState("");

  const goHome = (u) => {
    if (next) return navigate(next);
    navigate(u.role === "admin" ? "/admin" : u.role === "subscriber" ? "/dashboard" : "/browse");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const captchaToken = await recaptchaRef.current?.execute().catch(() => null);
    if (!captchaToken) { setError("CAPTCHA verification failed. Please try again."); setLoading(false); return; }
    try {
      const res = await login(email, password, captchaToken);
      if (res && res.two_factor_required) {
        setTwoFactor(res);
        if (res.dev_code) toast.info(`Dev mode: your code is ${res.dev_code}`);
        else toast.success(`Code sent to ${res.phone_hint}`);
      } else {
        goHome(res);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await verify2fa(twoFactor.user_id, code);
      goHome(u);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const sendEmailCode = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) { setError("Please enter your email address."); return; }
    setLoading(true);
    const captchaToken = await recaptchaRef.current?.execute().catch(() => null);
    if (!captchaToken) { setError("CAPTCHA verification failed. Please try again."); setLoading(false); return; }
    try {
      await requestEmailCode(email.trim(), captchaToken);
      setCodeSent(true);
      toast.success("If an account exists for that email, a 6-digit code is on its way. It expires in 10 minutes.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submitEmailCode = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await loginWithEmailCode(email.trim(), emailCode.trim());
      if (res && res.two_factor_required) {
        setTwoFactor(res);
        if (res.dev_code) toast.info(`Dev mode: your code is ${res.dev_code}`);
        else toast.success(`Code sent to ${res.phone_hint}`);
      } else {
        goHome(res);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const switchMode = (m) => { setMode(m); setError(""); setCodeSent(false); setEmailCode(""); setPassword(""); };

  if (twoFactor) {
    return (
      <div className="max-w-md mx-auto px-4 sm:px-6 py-20">
        <div className="flex items-center gap-2 text-primary"><ShieldCheck className="w-5 h-5" /><span className="text-xs uppercase tracking-[0.2em] font-bold">Two-factor</span></div>
        <h1 className="font-display text-4xl font-semibold tracking-tight mt-2">Enter your code</h1>
        <p className="text-muted-foreground mt-2">We sent a 6-digit code to {twoFactor.phone_hint}.</p>
        <form onSubmit={submitCode} className="mt-8 space-y-5">
          <div>
            <Label htmlFor="code">Verification code</Label>
            <Input id="code" value={code} onChange={(e) => setCode(e.target.value)} required maxLength={6}
              inputMode="numeric" placeholder="123456" className="mt-1.5 rounded-sm tracking-[0.5em] text-center" data-testid="twofa-code" />
          </div>
          {error && <p className="text-destructive text-sm" data-testid="twofa-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full rounded-sm" data-testid="twofa-submit">
            {loading ? "Verifying…" : "Verify & log in"}
          </Button>
          <button type="button" onClick={() => { setTwoFactor(null); setCode(""); }} className="text-sm text-muted-foreground hover:text-foreground" data-testid="twofa-back">
            ← Back to login
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-20">
      <h1 className="font-display text-4xl font-semibold tracking-tight">Welcome back</h1>
      <p className="text-muted-foreground mt-2">Log in to manage your bookings.</p>
      <div className="mt-8"><GoogleSignInButton /></div>

      {mode === "password" ? (
        <>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-1.5 rounded-sm" data-testid="login-email" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary font-medium" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-1.5 rounded-sm" data-testid="login-password" />
            </div>
            {error && <p className="text-destructive text-sm" data-testid="login-error">{error}</p>}
            <Recaptcha ref={recaptchaRef} action="login" />
            <Button type="submit" disabled={loading} className="w-full rounded-sm" data-testid="login-submit">
              {loading ? "Logging in…" : "Log in"}
            </Button>
          </form>
          <button type="button" onClick={() => switchMode("emailcode")} className="mt-4 text-sm text-primary font-medium hover:underline" data-testid="use-email-code">
            Email me a 6-digit code instead
          </button>
        </>
      ) : (
        <>
          {!codeSent ? (
            <form onSubmit={sendEmailCode} className="space-y-5">
              <div>
                <Label htmlFor="code-email">Email</Label>
                <Input id="code-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="mt-1.5 rounded-sm" data-testid="emailcode-email" />
                <p className="text-xs text-muted-foreground mt-1.5">We'll email you a 6-digit code that expires in 10 minutes.</p>
              </div>
              {error && <p className="text-destructive text-sm" data-testid="emailcode-error">{error}</p>}
              <Recaptcha ref={recaptchaRef} action="email_login" />
              <Button type="submit" disabled={loading} className="w-full rounded-sm" data-testid="send-email-code">
                {loading ? "Sending…" : "Send login code"}
              </Button>
            </form>
          ) : (
            <form onSubmit={submitEmailCode} className="space-y-5">
              <p className="text-sm text-muted-foreground">Enter the 6-digit code we sent to <span className="font-medium text-foreground">{email}</span>.</p>
              <div>
                <Label htmlFor="email-code">Login code</Label>
                <Input id="email-code" value={emailCode} onChange={(e) => setEmailCode(e.target.value)} required maxLength={6}
                  inputMode="numeric" placeholder="123456" className="mt-1.5 rounded-sm tracking-[0.5em] text-center" data-testid="emailcode-code" />
              </div>
              {error && <p className="text-destructive text-sm" data-testid="emailcode-verify-error">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full rounded-sm" data-testid="verify-email-code">
                {loading ? "Verifying…" : "Verify & log in"}
              </Button>
              <button type="button" onClick={() => { setCodeSent(false); setEmailCode(""); setError(""); }} className="text-sm text-muted-foreground hover:text-foreground" data-testid="emailcode-resend">
                ← Use a different email / resend code
              </button>
            </form>
          )}
          <button type="button" onClick={() => switchMode("password")} className="mt-4 text-sm text-primary font-medium hover:underline" data-testid="use-password">
            Log in with a password instead
          </button>
        </>
      )}
      <p className="mt-6 text-sm text-muted-foreground">
        No account? <Link to={next ? `/register?role=subscriber&next=${encodeURIComponent(next)}` : "/register"} className="text-primary font-medium">Sign up</Link>
      </p>
    </div>
  );
}
